import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import api from '../services/api';
import { useTenant } from '../context/TenantContext';

const KEY = 'vyomos_cookie_consent';
const PRIVATE_PREFIXES = ['/admin', '/parent', '/coach', '/staff', '/super-admin', '/player'];

const readChoice = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
};

// vyomos.io: Super Admin → Website → Settings. Academy sites: Admin → Homepage Editor → Cookie notice.
const normalize = (d, isMarketing) => (isMarketing
  ? { ...d, links: [{ label: d.link_label, url: d.marketing_link_url }] }
  : { ...d, links: [{ label: d.privacy_label, url: d.privacy_url }, { label: d.terms_label, url: d.terms_url }] });

function NoticeLink({ label, url, idx }) {
  const cls = 'underline font-medium text-slate-900';
  return url.startsWith('http')
    ? <a href={url} className={cls} data-testid={`cookie-notice-link-${idx}`}>{label}</a>
    : <Link to={url} className={cls} data-testid={`cookie-notice-link-${idx}`}>{label}</Link>;
}

export function CookieNotice() {
  const { branding, loading } = useTenant() || {};
  const [cfg, setCfg] = useState(null);
  const [hidden, setHidden] = useState(false);
  const isMarketing = !!branding?.is_platform;
  const { pathname } = useLocation();

  useEffect(() => {
    if (loading) return;
    const url = isMarketing ? '/public/website/cookie-notice' : '/homepage-config/cookie-notice';
    api.get(url).then(({ data }) => setCfg(normalize(data, isMarketing))).catch(() => setCfg(null));
  }, [loading, isMarketing]);

  if (!cfg || loading || hidden || !cfg.enabled) return null;
  if (PRIVATE_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  const prior = readChoice();
  if (prior && prior.version === cfg.version) return null;

  const choose = (choice) => {
    try { localStorage.setItem(KEY, JSON.stringify({ choice, version: cfg.version, at: new Date().toISOString() })); } catch (e) { /* ignore */ }
    window.dispatchEvent(new CustomEvent('cookie-consent', { detail: choice }));
    setHidden(true);
  };
  const links = cfg.links.filter((l) => l.label && l.url);

  return (
    <div className="fixed inset-x-3 bottom-3 sm:left-auto sm:right-5 sm:bottom-5 sm:max-w-md z-[60] rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-xl p-4 text-slate-700 animate-in fade-in slide-in-from-bottom-4 duration-300" role="dialog" aria-live="polite" data-testid="cookie-notice">
      <div className="flex gap-3">
        <Cookie className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
        <p className="text-sm leading-relaxed" data-testid="cookie-notice-message">
          {cfg.message}{' '}
          {links.map((l, i) => (
            <span key={i}>{i > 0 && ' · '}<NoticeLink {...l} idx={i} /></span>
          ))}
        </p>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" onClick={() => choose('declined')} className="px-4 py-1.5 text-sm rounded-full border border-slate-300 hover:bg-slate-100 transition-colors" data-testid="cookie-notice-decline">{cfg.decline_label || 'Decline'}</button>
        <button type="button" onClick={() => choose('accepted')} className="px-4 py-1.5 text-sm rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors" data-testid="cookie-notice-accept">{cfg.accept_label || 'Accept'}</button>
      </div>
    </div>
  );
}
